import { useEffect, useState } from 'react';
import {
  Grid,
  NativeSelect,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import Form from 'components/Form';
import { INames } from 'apis/main/type';
import { getNandaClass, getNandaDiagnosis } from 'apis/main';

interface Props {
  watch: any;
  setValue: any;
  register: any;
  disabled?: boolean;
  domainNames?: INames[];
  isTestMode?: boolean;

  nandaVersion: number;
  setNandaVersion: (v: number) => void;
}

function Nanda(props: Props) {
  const {
    watch,
    register,
    setValue,
    domainNames,
    disabled,
    isTestMode,
    nandaVersion,
    setNandaVersion,
  } = props;
  const m_domain = watch('domain');

  // GetNandaClass
  const m_class = watch('class');
  const [classNames, setClassNames] = useState<INames[]>([]);

  useEffect(() => {
    setValue('domain', 'none');
    setValue('class', 'none');
    setValue('diagnosis', 'none');
    setClassNames([]);
    setDiagnosisNames([]);
  }, [nandaVersion, setValue]);

  useEffect(() => {
    if (!m_domain || m_domain === 'none') {
      setClassNames([]);
      setDiagnosisNames([]);
      return;
    }

    getNandaClass({ version: nandaVersion, domain: m_domain }).then(
      ({ data }) => {
        setClassNames(data.names);
        setDiagnosisNames([]);
        setValue('class', 'none');
        setValue('diagnosis', 'none');
      }
    );
  }, [nandaVersion, m_domain, setValue]);

  // GetNandaDiagnosis
  const [diagnosisNames, setDiagnosisNames] = useState<INames[]>([]);
  useEffect(() => {
    if (!m_domain || m_domain === 'none') return;

    if (!m_class || m_class === 'none') {
      setDiagnosisNames([]);
      return;
    }

    getNandaDiagnosis({
      version: nandaVersion,
      domain: m_domain,
      class: m_class,
    }).then(({ data }) => setDiagnosisNames(data.names));
  }, [nandaVersion, m_domain, m_class]);

  // Render Select Options
  const getOptionValue = (option: INames) => option.eng;
  const getOptionLabel = (option: INames) => option.eng;

  const handlePreventEvent = (e: React.ClipboardEvent | React.MouseEvent) => {
    if (isTestMode) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Stack spacing={2}>
      <Grid
        container
        wrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        spacing={0.75}
      >
        <Grid item xs={4}>
          <Typography variant="caption">Хувилбар</Typography>
        </Grid>
        <Grid item xs={8}>
          <NativeSelect
            sx={{ fontSize: 14, pt: 1, width: '100%' }}
            value={nandaVersion}
            disabled={disabled}
            onChange={e => setNandaVersion(Number(e.target.value))}
            onMouseDown={handlePreventEvent}
            onContextMenu={handlePreventEvent}
          >
            <option value={12}>Хуучин хувилбар (v12.0)</option>
            <option value={13}>Шинэ хувилбар (v13.0)</option>
          </NativeSelect>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="caption">Домэйн</Typography>
        </Grid>
        <Grid item xs={8}>
          <Form.MuiSelect
            isNone
            disabled={disabled}
            options={domainNames}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            {...register('domain')}
          />
        </Grid>

        <Grid item xs={4}>
          <Typography variant="caption">Класс</Typography>
        </Grid>
        <Grid item xs={8}>
          <Form.MuiSelect
            isNone
            disabled={disabled}
            options={classNames}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            value={watch('class')}
            {...register('class')}
          />
        </Grid>

        <Grid item xs={4}>
          <Typography variant="caption">Онош</Typography>
        </Grid>
        <Grid item xs={8}>
          <Form.MuiSelect
            isNone
            disabled={disabled}
            options={diagnosisNames}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            value={watch('diagnosis')}
            {...register('diagnosis')}
          />
        </Grid>
      </Grid>

      <Form.Item label="Субъектив/Объектив өгөгдөл цуглуулах">
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          variant="outlined"
          disabled={disabled}
          {...register('collectingData')}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
        />
      </Form.Item>

      <Form.Item label="Богино/урт хугацааны зорилго">
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          variant="outlined"
          disabled={disabled}
          {...register('goal')}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
        />
      </Form.Item>

      <Form.Item label="Сувилахуйн төлөвлөгөө">
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          variant="outlined"
          disabled={disabled}
          {...register('plan')}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
        />
      </Form.Item>

      <Form.Item label="Хэрэгжүүлэлт">
        <TextField
          fullWidth
          multiline
          minRows={5}
          size="small"
          variant="outlined"
          disabled={disabled}
          {...register('interventions')}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
        />
      </Form.Item>

      <Form.Item label="Сувилахуйн үнэлгээ">
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          variant="outlined"
          disabled={disabled}
          {...register('evaluation')}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
        />
      </Form.Item>
    </Stack>
  );
}

export default Nanda;
